// qs-brush-engine-tapering.ts
import { useWebWorkers } from '@/composables/qs-useWebWorkers'
import type { KonvaGroup } from '@/types/qs-type-konva'
import type { SmoothingMode } from '@/types/qs-types-index'
import type { TaperingWorkerData } from '@/workers/qs-worker-tapering'
import Konva from 'konva'

export type TaperingType = 'pointed' | 'bow-tie' | 'thick-to-thin' | 'thin-to-thick'

export interface TaperingOptions {
  points: number[]
  originalStrokeWidth: number
  stroke: string
  opacity: number
  type: TaperingType
  returnVisualData?: boolean
  segmentCount?: number
  smoothingMode?: SmoothingMode
}

export interface VisualSegment {
  points: number[]
  strokeWidth: number
  opacity: number
}

export interface TaperingResult {
  group: KonvaGroup
  visualSegments?: VisualSegment[]
}

const strokeWidthCache = new Map<number, number>()
const MAX_CACHE_SIZE = 500

export class TaperingEngine {
  private static workerManager = useWebWorkers()

  static canCacheGroup(group: KonvaGroup): boolean {
    try {
      const rect = group.getClientRect()
      return rect.width > 0 && rect.height > 0
    } catch {
      return false
    }
  }

  static async createTaperedLine(options: TaperingOptions): Promise<TaperingResult> {
    const { points, originalStrokeWidth, stroke, opacity, type, returnVisualData = false, segmentCount, smoothingMode } = options


    if (points.length < 4) {
      throw new Error('Need at least 2 points for tapering')
    }

    if (smoothingMode === 'straight' && points.length === 4) {
      return this.createTaperedLineMainThread({
        ...options,
        segmentCount: 1
      })
    }

    if (this.workerManager.isAvailable && smoothingMode !== 'straight') {
      try {
        const workerData: TaperingWorkerData = {
          points,
          originalStrokeWidth,
          stroke,
          opacity,
          type,
          returnVisualData,
          segmentCount,
          smoothingMode
        }

        const workerResult = await this.workerManager.calculateTaperedLine(workerData)

        const taperedGroup = new Konva.Group({
          id: `tapered-group-${Date.now()}`,
          draggable: false
        })

        if (workerResult.visualSegments) {
          for (const segmentData of workerResult.visualSegments) {
            const segment = new Konva.Line({
              points: segmentData.points,
              stroke: stroke,
              strokeWidth: segmentData.strokeWidth,
              opacity: segmentData.opacity,
              lineCap: 'round',
              lineJoin: 'round',
              draggable: false
            })

            taperedGroup.add(segment)
          }
        }

        if (workerResult.segmentCount > 10 && this.canCacheGroup(taperedGroup)) {
          taperedGroup.cache()
          TaperingEngine.setupProgressiveSharpness(taperedGroup)
        }

        const result: TaperingResult = { group: taperedGroup }
        if (returnVisualData && workerResult.visualSegments) {
          result.visualSegments = workerResult.visualSegments
        }

        return result
      } catch (error) {
        console.warn('Web Worker failed, falling back to main thread:', error)
      }
    }

    return this.createTaperedLineMainThread(options)
  }


  private static createTaperedLineMainThread(options: TaperingOptions): TaperingResult {
    const { points, originalStrokeWidth, stroke, opacity, type, returnVisualData = false, segmentCount } = options

    let finalSegmentCount = segmentCount

    if (!finalSegmentCount) {
      const pointCount = points.length / 2
      const originalSegmentCount = Math.floor(pointCount) - 1
      
      if (pointCount === 2) {
        finalSegmentCount = 1
      } else {
        finalSegmentCount = Math.max(5, originalSegmentCount)
      }
    }

    const taperedGroup = new Konva.Group({
      id: `tapered-group-${Date.now()}`,
      draggable: false
    })

    const visualSegments: VisualSegment[] = []

    if (finalSegmentCount === 1) {
      const startX = points[0]
      const startY = points[1]
      const endX = points[points.length - 2]
      const endY = points[points.length - 1]
      
      if (isFinite(startX) && isFinite(startY) && isFinite(endX) && isFinite(endY)) {
        const minSegments = 20
        for (let i = 0; i < minSegments; i++) {
          const progress = i / (minSegments - 1)
          const nextProgress = i === minSegments - 1 ? 1.0 : (i + 1) / (minSegments - 1)
          
          const startPointX = startX + (endX - startX) * progress
          const startPointY = startY + (endY - startY) * progress
          const endPointX = startX + (endX - startX) * nextProgress
          const endPointY = startY + (endY - startY) * nextProgress
          
          const segmentProgress = (progress + nextProgress) / 2
          const strokeWidth = this.calculateStrokeWidth(segmentProgress, originalStrokeWidth, type)
          
          if (isFinite(strokeWidth) && strokeWidth > 0) {
            if (returnVisualData) {
              visualSegments.push({
                points: [startPointX, startPointY, endPointX, endPointY],
                strokeWidth: Math.max(strokeWidth, 0.5),
                opacity: opacity
              })
            }
            
            const segment = new Konva.Line({
              points: [startPointX, startPointY, endPointX, endPointY],
              stroke: stroke,
              strokeWidth: Math.max(strokeWidth, 0.5),
              opacity: opacity,
              lineCap: 'round',
              lineJoin: 'round',
              draggable: false
            })
            
            taperedGroup.add(segment)
          }
        }
      }
    } else {
      for (let i = 0; i < finalSegmentCount; i++) {
        const progress = i / (finalSegmentCount - 1)
        const nextProgress = (i + 1) / (finalSegmentCount - 1)

        const startPoint = this.getPointAtProgress(points, progress)
        const endPoint = this.getPointAtProgress(points, nextProgress)

        if (!startPoint || !endPoint) continue

        if (!this.isValidPoint(startPoint) || !this.isValidPoint(endPoint)) {
          continue
        }

        const strokeWidth = this.calculateStrokeWidth(progress, originalStrokeWidth, type)

        if (!isFinite(strokeWidth) || strokeWidth <= 0) continue

        if (returnVisualData) {
          visualSegments.push({
            points: [startPoint.x, startPoint.y, endPoint.x, endPoint.y],
            strokeWidth: Math.max(strokeWidth, 0.5),
            opacity: opacity
          })
        }

        const overlapFactor = type === 'pointed' ? 0.15 : 0.1
        const dx = endPoint.x - startPoint.x
        const dy = endPoint.y - startPoint.y
        const segmentLength = Math.sqrt(dx * dx + dy * dy)

        if (segmentLength > 0) {
          const overlapDistance = segmentLength * overlapFactor
          const directionX = dx / segmentLength
          const directionY = dy / segmentLength

          const extendedEndPoint = {
            x: endPoint.x + directionX * overlapDistance,
            y: endPoint.y + directionY * overlapDistance
          }

          const segment = new Konva.Line({
            points: [startPoint.x, startPoint.y, extendedEndPoint.x, extendedEndPoint.y],
            stroke: stroke,
            strokeWidth: Math.max(strokeWidth, 0.5),
            opacity: opacity,
            lineCap: 'round',
            lineJoin: 'round',
            draggable: false
          })

          taperedGroup.add(segment)
        } else {
          const segment = new Konva.Line({
            points: [startPoint.x, startPoint.y, endPoint.x, endPoint.y],
            stroke: stroke,
            strokeWidth: Math.max(strokeWidth, 0.5),
            opacity: opacity,
            lineCap: 'round',
            lineJoin: 'round',
            draggable: false
          })

          taperedGroup.add(segment)
        }
      }
    }

    const groupAsAny = taperedGroup as unknown as { children?: (() => unknown[]) | unknown[] }
    const childrenCount = groupAsAny.children
      ? typeof groupAsAny.children === 'function'
        ? (groupAsAny.children as () => unknown[])().length
        : Array.isArray(groupAsAny.children)
          ? groupAsAny.children.length
          : 0
      : 0
    if (finalSegmentCount > 10 && childrenCount > 0 && this.canCacheGroup(taperedGroup)) {
      taperedGroup.cache()
      TaperingEngine.setupProgressiveSharpness(taperedGroup)
    }

    const result: TaperingResult = { group: taperedGroup }
    if (returnVisualData) {
      result.visualSegments = visualSegments
    }

    return result
  }

  private static calculateStrokeWidth(progress: number, originalStrokeWidth: number, type: TaperingType): number {
    const typeHash = type === 'pointed' ? 1 : type === 'bow-tie' ? 2 : type === 'thick-to-thin' ? 3 : type === 'thin-to-thick' ? 4 : 0
    const cacheKey = Math.round(progress * 1000) + (typeHash * 100000)

    if (strokeWidthCache.has(cacheKey)) {
      return strokeWidthCache.get(cacheKey)! * originalStrokeWidth
    }

    if (strokeWidthCache.size > MAX_CACHE_SIZE * 0.8) {
      this.clearCache()
    }

    let strokeWidth: number

    switch (type) {
      case 'pointed':
        strokeWidth = this.calculatePointedStrokeWidth(progress)
        break
      case 'bow-tie':
        strokeWidth = this.calculateBowTieStrokeWidth(progress)
        break
      case 'thick-to-thin':
        strokeWidth = this.calculateThickToThinStrokeWidth(progress)
        break
      case 'thin-to-thick':
        strokeWidth = this.calculateThinToThickStrokeWidth(progress)
        break
      default:
        strokeWidth = 1.0
    }

    strokeWidthCache.set(cacheKey, strokeWidth)
    return strokeWidth * originalStrokeWidth
  }

  private static calculatePointedStrokeWidth(progress: number): number {
    const smoothProgress = progress * progress * (3 - 2 * progress)
    const sineValue = Math.sin(smoothProgress * Math.PI)
    return 0.2 + (sineValue * 0.8)
  }

  private static calculateBowTieStrokeWidth(progress: number): number {
    const centerDistance = Math.abs(progress - 0.5) * 2
    const curve = centerDistance * centerDistance
    return 0.4 + (curve * 0.6)
  }

  private static calculateThickToThinStrokeWidth(progress: number): number {
    return 1.0 - (progress * 0.7)
  }

  private static calculateThinToThickStrokeWidth(progress: number): number {
    return 0.3 + (progress * 0.7)
  }

  private static getPointAtProgress(points: number[], progress: number): { x: number; y: number } | null {
    if (points.length < 4) return null

    const totalLength = this.calculateLineLength(points)
    if (totalLength === 0) {
      return { x: points[0] || 0, y: points[1] || 0 }
    }

    const targetLength = totalLength * progress
    let currentLength = 0

    for (let i = 0; i < points.length - 2; i += 2) {
      const x1 = points[i]
      const y1 = points[i + 1]
      const x2 = points[i + 2]
      const y2 = points[i + 3]

      if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) continue

      const dx = x2 - x1
      const dy = y2 - y1
      const segmentLength = Math.sqrt(dx * dx + dy * dy)

      if (segmentLength === 0) continue

      if (currentLength + segmentLength >= targetLength) {
        const segmentProgress = (targetLength - currentLength) / segmentLength
        return {
          x: (x1 || 0) + dx * segmentProgress,
          y: (y1 || 0) + dy * segmentProgress
        }
      }

      currentLength += segmentLength
    }

    return {
      x: points[points.length - 2] || 0,
      y: points[points.length - 1] || 0
    }
  }

  private static calculateLineLength(points: number[]): number {
    let totalLength = 0
    for (let i = 0; i < points.length - 2; i += 2) {
      const x1 = points[i]
      const y1 = points[i + 1]
      const x2 = points[i + 2]
      const y2 = points[i + 3]

      if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) continue

      const dx = x2 - x1
      const dy = y2 - y1
      const segmentLength = Math.sqrt(dx * dx + dy * dy)

      if (isFinite(segmentLength)) {
        totalLength += segmentLength
      }
    }
    return totalLength
  }

  private static isValidPoint(point: { x: number; y: number }): boolean {
    return isFinite(point.x) && isFinite(point.y)
  }

  private static clearCache(): void {
    const entries = Array.from(strokeWidthCache.entries())
    strokeWidthCache.clear()

    entries.slice(-Math.floor(MAX_CACHE_SIZE / 2)).forEach(([key, value]) => {
      strokeWidthCache.set(key, value)
    })
  }

  static clearAllCaches(): void {
    strokeWidthCache.clear()
  }

  private static sharpnessTimeouts = new WeakMap<KonvaGroup, number>()
  private static cachedGroups = new Set<KonvaGroup>()
  private static zoomActivityTimeout: number | null = null

  static setupProgressiveSharpness(group: KonvaGroup): void {
    this.cachedGroups.add(group)

    const clearCacheDelayed = () => {
      const timeout = window.setTimeout(() => {
        if (group && group.getStage()) {
          group.clearCache()
        } else {
          this.cachedGroups.delete(group)
        }
        this.sharpnessTimeouts.delete(group)
      }, 1000)

      this.sharpnessTimeouts.set(group, timeout)
    }

    clearCacheDelayed()
  }

  static onZoomActivity(): void {
    const groupsToRemove: KonvaGroup[] = []
    this.cachedGroups.forEach(group => {
      if (group && group.getStage()) {
        group.cache()
      } else {
        groupsToRemove.push(group)
      }
    })
    groupsToRemove.forEach(group => this.cachedGroups.delete(group))

    this.resetProgressiveSharpnessTimeout()
  }

  private static resetProgressiveSharpnessTimeout(): void {
    if (this.zoomActivityTimeout) {
      clearTimeout(this.zoomActivityTimeout)
    }

    this.zoomActivityTimeout = window.setTimeout(() => {
      const groupsToRemove: KonvaGroup[] = []
      this.cachedGroups.forEach(group => {
        if (group && group.getStage()) {
          group.clearCache()
        } else {
          groupsToRemove.push(group)
        }
      })
      groupsToRemove.forEach(group => this.cachedGroups.delete(group))
    }, 1000)
  }

  static cleanupSharpnessTimeouts(): void {
    if (this.zoomActivityTimeout) {
      clearTimeout(this.zoomActivityTimeout)
      this.zoomActivityTimeout = null
    }
    
    const groupsToRemove: KonvaGroup[] = []
    this.cachedGroups.forEach(group => {
      if (!group || !group.getStage()) {
        groupsToRemove.push(group)
      }
    })
    groupsToRemove.forEach(group => {
      this.cachedGroups.delete(group)
      this.sharpnessTimeouts.delete(group)
    })
  }
}